import type { FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import type { ImageSource } from '../../../types'
import { AnimationWrapper } from '../../AnimationWrapper'
import { BaseLink } from '../../Links'
import { Picture } from '../../Picture'
import { TypographyV2 } from '../../Seatbelt2/Foundations/Typography'
import styles from './EditorialCard.module.scss'

export const EditorialCard: FunctionComponent<Props> = ({
  className,
  date,
  href,
  image = '',
  imageAlt = '',
  imageSources,
  title,
}) => (
  <article
    className={classnames(styles.editorial, className)}
    data-testid="editorial-card"
  >
    <BaseLink
      href={href}
      className={styles.imageLink}
      data-testid="editorial-card-image-link"
    >
      <Picture
        alt={imageAlt}
        aspectRatioMain="two-to-one"
        sources={imageSources}
        src={image}
      />
    </BaseLink>
    <BaseLink
      href={href}
      className={styles.baseLink}
      invertedUnderline={true}
      data-testid="editorial-card-text-link"
    >
      <TypographyV2
        tag="h2"
        textStyle="sb-text-body-default-strong"
        className={styles.title}
        data-testid="editorial-card-text-title"
      >
        <AnimationWrapper inverted={true}>{title}</AnimationWrapper>
      </TypographyV2>
      {date && (
        <TypographyV2
          textStyle="sb-text-interface-body-small-regular"
          data-testid="editorial-card-text-date"
        >
          {date}
        </TypographyV2>
      )}
    </BaseLink>
  </article>
)

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Link that will be visited when the card is clicked on.
   */
  href: string
  /**
   * Title that will appear within the article card.
   */
  title: string
  /**
   * Article date to appear at the bottom of the card. N.B This will not appear if an author prop is also provided.
   */
  date?: string
  /**
   * Image link to be displayed at the top of the article card if provided.
   */
  image?: string
  /**
   * Article image data for responsive images.
   */
  imageSources?: ImageSource[]
  /**
   * Text to be used in the Image alt tag.
   */
  imageAlt?: string
  /**
   * Class name that can be added to add extra styling to the component
   */
  className?: string
  /**
   * A group of articles to render via the LinksListExpandable component.
   */
  articles?: Array<{
    label: string
    href: string
  }>
  /**
   * Outermost HTML element to be rendered. Defaults to 'article'.
   */
  tag?: keyof JSX.IntrinsicElements
}
